import {useEffect, useRef} from 'react'
import {useSearchParams, useParams, useNavigate, useLocation} from 'react-router-dom'
import DebugScene from '../components/DebugScene'
import EditorPage from '../components/EditorPage'
import LeaveGuardModal from '../components/LeaveGuardModal'
import {EditorConfigProvider} from '../components/EditorConfigContext'
import {useAppDispatch, store} from '../../common/store'
import {loadProject} from '../../common/components/store/scenesSlice'
import {triggerCameraPreset} from '../../common/components/deviceMesh/cameraPresetStore'
import {useEditorConfig} from '../../common/hooks/useEditorConfig'
import useLeaveGuard from '../hooks/useLeaveGuard'
import type {SceneFullConfig} from '../../common/components/types'
import {serializeConfig} from '../components/topMenuBar/serializeConfig'
import {setSavedSnapshot, clearSavedSnapshot} from '../../common/components/store/savedSnapshotStore'
import {setProjectName, clearProjectName} from '../../common/components/store/projectNameStore'
import {setProjectId, clearProjectId} from '../../common/components/store/projectIdStore'
import {stableStringify} from '../../common/utils/stableStringify'
import {getProject} from '../../common/api/generated/endpoints/projects/projects'
import {getMyProject} from '../../common/api/generated/endpoints/my-projects/my-projects'

const EditorRouteInner = () => {
  const [searchParams] = useSearchParams()
  const {projectId, projectSlug} = useParams()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {deviceConfig} = useEditorConfig()
  const appliedRef = useRef(false)
  const isDebug = searchParams.has('debug')
  const {blocker} = useLeaveGuard(deviceConfig)

  // Clear snapshot + project name + project id on unmount to avoid stale data across editor sessions
  useEffect(
    () => () => {
      clearSavedSnapshot()
      clearProjectName()
      clearProjectId()
    },
    [],
  )

  // Load a saved project by ID
  useEffect(() => {
    if (!projectId || appliedRef.current) return
    appliedRef.current = true

    const fetchProject = async () => {
      try {
        const {data} = await getMyProject(projectId)
        const scenes = data.scenes.map((s) => s.config as unknown as SceneFullConfig)
        const sceneNames = data.scenes.map((s) => s.name)

        setProjectName(data.name)
        setProjectId(data.id)
        dispatch(loadProject({scenes, deviceConfig, sceneNames}))
        setSavedSnapshot(stableStringify(serializeConfig(store.getState().present, deviceConfig)))

        // Restore camera from first scene
        const firstCamera = (data.scenes[0]?.config as Record<string, unknown>)?.camera as
          | {position: number[]; rotation: number[]}
          | undefined
        if (firstCamera) {
          triggerCameraPreset(firstCamera.position, firstCamera.rotation)
        }
      } catch {
        navigate('/my-projects')
      }
    }

    fetchProject()
  }, [projectId, dispatch, deviceConfig, navigate])

  // Apply a public project as a scene preset (via projectSlug)
  useEffect(() => {
    if (!projectSlug || projectId || appliedRef.current) return
    appliedRef.current = true

    const fetchAndApply = async () => {
      try {
        const {data} = await getProject(projectSlug)

        // Strip screen images when forking a user-authored project (author's screenshots are private)
        if (data.author) {
          for (const s of data.scenes) {
            const cfg = s.config as Record<string, unknown>
            delete cfg['textureKey']
            delete cfg['textureUrl']
          }
        }

        const scenes = data.scenes.map((s) => s.config as unknown as SceneFullConfig)
        const sceneNames = data.scenes.map((s) => s.name)

        setProjectName(data.name)
        dispatch(loadProject({scenes, deviceConfig, sceneNames}))
        setSavedSnapshot(stableStringify(serializeConfig(store.getState().present, deviceConfig)))

        const firstCamera = (data.scenes[0]?.config as Record<string, unknown>)?.camera as
          | {position: number[]; rotation: number[]}
          | undefined
        if (firstCamera) {
          triggerCameraPreset(firstCamera.position, firstCamera.rotation)
        }
      } catch {
        // Silently ignore — user gets default scene
      }
    }

    fetchAndApply()
  }, [projectSlug, projectId, dispatch, deviceConfig])

  if (isDebug) return <DebugScene />
  return (
    <>
      <EditorPage />
      <LeaveGuardModal blocker={blocker} />
    </>
  )
}

// `key={pathname}` forces a full remount of EditorConfigProvider + EditorRouteInner whenever the
// URL changes — between two saved projects (`/editor/:id` → `/editor/:other-id`) or to/from the
// new-project route (`/editor/new`). Without this, react-router keeps the same component instance
// across same-pattern routes and stale Redux state from the previous project leaks into the new
// view (visible as a phantom leave guard prompt and as "new project" inheriting the prior scene).
const EditorRoute = () => {
  const location = useLocation()
  return (
    <EditorConfigProvider scope="editor" key={location.pathname}>
      <EditorRouteInner />
    </EditorConfigProvider>
  )
}

export default EditorRoute
